import React, { useState } from 'react';
import axios from 'axios';
import RenderError from './RenderError';
import { fuelTypeOptions, equipmentTypes } from './config';
import Success from './Success';
import Submit from './Submit';

function ReplacementAssessment({
  applicationData: {
    aid,
    equipmentType,
    replacing,
    fuelTypes,
    acresToIrrigate,
    acreInchesOrGallons
  },
  setApplicationData,
  initialView,
  setView
}) {
  const [formData, setFormData] = useState({
    aid: aid ?? '',
    replacing: replacing ?? '',
    fuelTypes: fuelTypes ?? {},
    acresToIrrigate: acresToIrrigate ?? '',
    acreInchesOrGallons: acreInchesOrGallons ?? ''
  });
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const handleCheckFuelType = (event) => {
    const { value, checked } = event.target;

    if (value in formData.fuelTypes) {
      let newFormData = formData.fuelTypes;
      delete newFormData[value];
      setFormData({ ...formData, fuelTypes: newFormData });
    } else {
      setFormData({
        ...formData,
        fuelTypes: {
          ...formData.fuelTypes,
          [value]: value === 'Other engine type (please specify)' ? '' : value
        }
      });
    }
  };

  const handleInputChange = (event) => {
    const { value, name } = event.target;

    if (name === 'fuelTypes_other') {
      setFormData({
        ...formData,
        fuelTypes: {
          ...formData.fuelTypes,
          ['Other engine type (please specify)']: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    setErrors([]);
    setSuccess('');
    const actions = {
      irrigationEngine: 'irrigation_engine_replacement_assessment',
      buildingHeat: 'building_heat_replacement_assessment',
      waterHeating: 'water_heating_replacement_assessment',
      powerGenerator: 'power_generator_replacement_assessment'
    };

    axios
      .post(
        window.wlPipConfig.ajaxUrl +
          `?action=wl_fip_save_${actions[equipmentType]}`,
        formData
      )
      .then((res) => {
        setLoading(false);
        setSuccess(res.data.data.message);
        setApplicationData(res.data.data.applicationData);
        setTimeout(() => setView(initialView), 1000);
      })
      .catch((err) => {
        setLoading(false);
        setErrors(err.response.data.data);
      });
  };

  return (
    <div className="fip-unit-form">
      <div className="fip-unit-form-fields">
        <div className="grid__col-xs-12 u--padding-left-0">
          <div className="form__field form__field--required">
            <label className="form__label">
              Are you replacing a(n) {equipmentTypes[equipmentType]}(s) with the
              propane-fueled equipment you purchased through the Propane Farm
              Incentive Program? (select one)
            </label>
            {errors.replacing && <RenderError error={errors.replacing} />}
            <label className="form__radio">
              <input
                type="radio"
                name="replacing"
                value="yes"
                onChange={handleInputChange}
                checked={formData.replacing === 'yes' && 'checked'}
              />
              Yes - I am replacing {equipmentTypes[equipmentType]}(s)
            </label>
            <label className="form__radio">
              <input
                type="radio"
                name="replacing"
                value="no"
                onChange={handleInputChange}
                checked={formData.replacing === 'no' && 'checked'}
              />
              No - I am not replacing {equipmentTypes[equipmentType]}(s), just
              adding another to my operation
            </label>
          </div>
          {formData.replacing === 'yes' && (
            <div className="form__field form__field--required">
              <label className="form__label">
                Which {equipmentTypes[equipmentType]} fuel types are you
                replacing with your {equipmentTypes[equipmentType]}(s)? (select
                all that apply)
              </label>
              {errors.fuelTypes && <RenderError error={errors.fuelTypes} />}
              {Object.values(fuelTypeOptions).map((type) => (
                <div key={type}>
                  <label className="form__checkbox" key={type}>
                    <input
                      type="checkbox"
                      value={type}
                      checked={type in formData.fuelTypes}
                      onChange={handleCheckFuelType}
                    />
                    {type}
                  </label>
                  {type in formData.fuelTypes &&
                    (type === 'Other engine type (please specify)' ? (
                      <div className="u--margin-top-10 u--margin-left-10">
                        <input
                          type="text"
                          name="fuelTypes_other"
                          value={formData.fuelTypes[type]}
                          onChange={handleInputChange}
                          className="form__input form__input--lg u--width-100"
                        />
                        {errors[type] && <RenderError error={errors[type]} />}
                      </div>
                    ) : (
                      ''
                    ))}
                </div>
              ))}
            </div>
          )}
          {equipmentType === 'irrigationEngine' && (
            <div className="form__field form__field--required">
              <label className="form__label">
                How many acres do you plan to irrigate using the new
                propane-fueled irrigation engine?
              </label>
              <span className="u--margin-right-10">Acres</span>
              <input
                type="number"
                value={formData.acresToIrrigate}
                className="form__input form__input--lg u--margin-bottom-10"
                name="acresToIrrigate"
                onChange={handleInputChange}
              />
              {errors.acresToIrrigate && (
                <RenderError error={errors.acresToIrrigate} />
              )}
              <span className="u--margin-right-10">Acre-inches or gallons</span>
              <input
                type="number"
                value={formData.acreInchesOrGallons}
                className="form__input form__input--lg u--margin-bottom-10"
                name="acreInchesOrGallons"
                onChange={handleInputChange}
              />
              {errors.acreInchesOrGallons && (
                <RenderError error={errors.acreInchesOrGallons} />
              )}
            </div>
          )}
          <Submit
            loading={loading}
            handleSubmit={handleSubmit}
            text="Save Section"
            errors={errors}
            disabled={!Boolean(aid)}
          />
          {success && <Success success={success} />}
        </div>
      </div>
    </div>
  );
}

ReplacementAssessment.displayName = 'ReplacementAssessment'; // because WebPack renames the function
export default ReplacementAssessment;
